import React, { useEffect, useMemo } from "react";
import { Modal, Form, Input, Button, message, Row, Col } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/CreateCredential/action";
import "../agent.scss";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import scopeOptions from "./Scopes";
const CreateCredentialForm = ({
  visible,
  onCancel,
  setVisible,
  loading,
  editingRecord,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const animatedComponents = makeAnimated();

  const { messages, error, buttonLoading } = useSelector(
    (state) => state.AgentReducer
  );

const scopeOptionsMemo = useMemo(() => scopeOptions, []);
const optionsWithSelectAll = useMemo(
  () => [{ value: "select_all", label: "Select All" }, ...scopeOptions],
  [scopeOptions]
);



  const generateClientKey = () => uuidv4();

  const generateClientSecret = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789./";
    let result = "$2b$10$";
    for (let i = 0; i < 53; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  useEffect(() => {
    if (editingRecord) {
      const selectedScopes = editingRecord.scopes.split(" ").map((scope) => {
        const found = scopeOptions.find((opt) => opt.value === scope);
        return found || { value: scope, label: scope };
      });
      form.setFieldsValue({
        orgId: editingRecord.orgId,
        companyName: editingRecord.companyName,
        phone: editingRecord.phone,
        scopes: selectedScopes,
      });
    } else {
      form.resetFields();
    }
  }, [editingRecord, form, scopeOptions]);

  useEffect(() => {
    if (messages) {
      messageApi.success(messages);
      dispatch(actions.addAgentSuccess(null));
      setVisible(false);
      form.resetFields();
    }
  }, [messages, messageApi, dispatch, form, setVisible]);

  useEffect(() => {
    if (error) {
      messageApi.error(error);
      dispatch(actions.addAgentFailed(null));
    }
  }, [error, messageApi, dispatch]);

  const handleScopeChange = (selectedOptions, actionMeta) => {
    const isSelectAll = actionMeta.option?.value === "select_all";

    if (isSelectAll) {
      form.setFieldsValue({
        scopes: [{ value: "select_all", label: "Select All" }, ...scopeOptions],
      });
      return;
    }

    const currentValues =
      selectedOptions?.filter((opt) => opt.value !== "select_all") || [];

    const allSelected = currentValues.length === scopeOptions.length;

    const newValue = allSelected
      ? [{ value: "select_all", label: "Select All" }, ...scopeOptions]
      : currentValues;

    form.setFieldsValue({ scopes: newValue });
  };


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const scopesString = values.scopes
        ? values.scopes
            .filter((opt) => opt.value !== "select_all")
            .map((opt) => opt.value)
            .join(" ")
        : "";

      if (editingRecord) {
        const updatePayload = {
          orgId: values.orgId,
          companyName: values.companyName,
          phone: values.phone,
          scopes: scopesString,
        };
        dispatch(actions.updateAgent(editingRecord._id, updatePayload));
      } else {
        const createPayload = {
          ...values,
          scopes: scopesString,
          clientKey: values.clientKey || generateClientKey(),
          clientSecret: values.clientSecret || generateClientSecret(),
        };
        dispatch(actions.addAgent(createPayload));
      }
    } catch (error) {
      console.error("Form validation error:", error);
      messageApi.error(error.message || "Please fix the form errors");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const validateOrgId = (_, value) => {
    if (value && value.length !== 20) {
      return Promise.reject("Organization ID must be exactly 20 characters");
    }
    return Promise.resolve();
  };

  const validatePhoneNumber = (_, value) => {
    if (value && (value.length !== 10 || isNaN(value))) {
      return Promise.reject("Phone number must be 10 digits");
    }
    return Promise.resolve();
  };

  const validateClientKey = (_, value) => {
    if (value && value.length !== 36) {
      return Promise.reject("Client Key must be exactly 36 characters");
    }
    return Promise.resolve();
  };

  const validateClientSecret = (_, value) => {
    if (value && value.length !== 60) {
      return Promise.reject("Client Secret must be exactly 60 characters");
    }
    return Promise.resolve();
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "32px",
      height: "auto",
      borderRadius: "5px",
      borderColor: "#d9d9d9",
      "&:hover": {
        borderColor: "#40a9ff",
      },
      padding: state.selectProps.value?.length > 2 ? "4px 0" : "0",
    }),
   valueContainer: (provided) => ({
  ...provided,
  height: "auto",
  maxHeight: "120px", // Set your desired max height
  overflowY: "auto", // Enables vertical scrolling
  padding: "4px 8px",
  flexWrap: "wrap",
  alignItems: "flex-start",
   scrollbarWidth: "none",       // Firefox
  msOverflowStyle: "none",      // IE and Edge
  "&::-webkit-scrollbar": {
    display: "none",            // Chrome, Safari
  },
}),

    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0",
  
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "32px",
      
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#f0f0f0",
      margin: "2px",
      
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "11px",
      padding: "2px 2px",
   
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      ":hover": {
        backgroundColor: "transparent",
        color: "#ff4d4f",
      },
    }),
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={editingRecord ? "Edit Organizer" : "Add Organizer"}
        visible={visible}
        onCancel={handleCancel}
        width={780}
        maskClosable={false}
        className="input"
        style={{ top: 100 }}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={buttonLoading}
            onClick={handleSubmit}
            style={{ backgroundColor: "#073763", borderColor: "#073763" }}
          >
            {editingRecord ? "Update" : "Submit"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="orgId"
                label="Organization ID"
                rules={[
                  { required: true, message: "Please enter Organization ID" },
                  { validator: validateOrgId },
                ]}
              >
                <Input placeholder="Enter Organization ID" maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true, message: "Please enter Company Name" }]}
              >
                <Input placeholder="Enter Company Name" />
              </Form.Item>
            </Col>
          </Row>

          {!editingRecord && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="clientKey"
                  label="Client Key"
                  rules={[
                    { required: true, message: "Please enter Client Key" },
                    { validator: validateClientKey },
                  ]}
                >
                  <Input
                    placeholder="Enter Client Key"
                    maxLength={36}
                    addonAfter={
                      <Button
                        type="link"
                        onClick={() =>
                          form.setFieldsValue({
                            clientKey: generateClientKey(),
                          })
                        }
                      >
                        Generate
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="clientSecret"
                  label="Client Secret"
                  rules={[
                    { required: true, message: "Please enter Client Secret" },
                    { validator: validateClientSecret },
                  ]}
                >
                  <Input
                    placeholder="Enter Client Secret"
                    maxLength={60}
                    addonAfter={
                      <Button
                        type="link"
                        onClick={() =>
                          form.setFieldsValue({
                            clientSecret: generateClientSecret(),
                          })
                        }
                      >
                        Generate
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject("Phone number is required");
                      }
                      if (!/^\d{10}$/.test(value)) {
                        return Promise.reject("Phone number must be exactly 10 digits");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="tel"
                  placeholder="Enter Phone Number"
                  maxLength={10}
                  onWheel={(e) => e.target.blur()}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="scopes"
                label="Scopes"
                rules={[{ required: true, message: "Please select at least one scope" }]}
              >
                <Select
                  closeMenuOnSelect={false}
                  components={animatedComponents}
                  isMulti
                  placeholder="Select scopes..."
                  styles={customStyles}
                  onChange={handleScopeChange}
                  value={form.getFieldValue("scopes")}
                  options={(() => {
                    const selected = form.getFieldValue("scopes") || [];
                    const selectedValues = selected.map((opt) => opt.value);
                    if (selectedValues.includes("select_all")) {
                      return [{ value: "select_all", label: "Select All" }];
                    }
                    return optionsWithSelectAll.filter(
                      (opt) => !selectedValues.includes(opt.value)
                    );
                  })()}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CreateCredentialForm;
